import {
  BoxGeometry,
  Group,
  Mesh,
  MeshStandardMaterial,
} from "three";
import type { LevelDefinition } from "./types";
import { createMissionStateMachine, type MissionDefinition } from "../scripting/api";

const floorMaterial = new MeshStandardMaterial({ color: "#1a2230", roughness: 0.95, metalness: 0.02 });
const platformMaterial = new MeshStandardMaterial({ color: "#293548", roughness: 0.92, metalness: 0.08 });
const shadowMaterial = new MeshStandardMaterial({ color: "#10151d", roughness: 0.9, metalness: 0.05 });
const accentMaterial = new MeshStandardMaterial({
  color: "#f97316",
  roughness: 0.45,
  metalness: 0.25,
  emissive: "#f97316",
  emissiveIntensity: 0.18,
});

const arenaSurvivalMission: MissionDefinition = {
  id: "arena-survival",
  name: "Arena Survival",
  description: "Clear Cortex sentry waves and hold the arena.",
  createMission: () => {
    const state = createMissionStateMachine<"warmup" | "combat" | "clear">("warmup");

    return {
      init(context) {
        context.objectives.addObjective("survive", "Survive the live-fire calibration.", "primary");
        context.objectives.addObjective("clear-wave", "Eliminate the current Cortex wave.");
        context.setStatus("Mission uplink ready. Enter the arena to begin calibration.");
      },
      onStart(context) {
        state.setState("combat");
        context.setStatus("Arena Survival live. Neutralize all Cortex sentries.");
      },
      onUpdate(_dt, context) {
        if (state.getState() === "combat" && context.getEnemiesRemaining() === 0) {
          state.setState("clear");
          context.objectives.complete("clear-wave");
          context.setStatus("Wave clear. Additional Cortex sentries are inbound.");
          context.eventBus.emit("mission:wave-cleared", { missionId: "arena-survival" });
        }
      },
      onEvent(event, context) {
        if (event.type === "wave:started") {
          state.setState("combat");
          context.objectives.setPrimary("clear-wave");
          context.setStatus(`Wave ${(event.payload?.wave as number) ?? "?"} live. Hold the arena.`);
        }

        if (event.type === "player:damaged") {
          context.setState("lastDamageAmount", (event.payload?.amount as number) ?? 0);
        }
      },
    };
  },
};

export const arena01: LevelDefinition = {
  id: "arena01",
  name: "Arena-01",
  description: "A compact neon combat bowl with layered cover, flag bases, and a central kill box.",
  thumbnail: "/os/assets/cortex/maps/arena01.png",
  supportedModes: ["survival", "dm", "tdm", "ctf"],
  spawnPoints: {
    singleplayer: [{ id: "sp-01", position: [0, 2.8, 16], yaw: Math.PI }],
    multiplayer: [
      { id: "mp-01", position: [-18, 1.2, -18], team: "alpha" },
      { id: "mp-02", position: [18, 1.2, -18], team: "alpha" },
      { id: "mp-03", position: [-18, 1.2, 18], team: "bravo" },
      { id: "mp-04", position: [18, 1.2, 18], team: "bravo" },
      { id: "mp-05", position: [0, 2.8, -12] },
      { id: "mp-06", position: [0, 2.8, 12] },
    ],
  },
  triggerVolumes: [
    { id: "center-ring", kind: "box", center: [0, 1.5, 0], size: [8, 3, 8], event: "trigger:center-ring" },
    { id: "alpha-base", kind: "box", center: [-18, 1.5, -18], size: [4, 3, 4], event: "trigger:alpha-base" },
    { id: "bravo-base", kind: "box", center: [18, 1.5, 18], size: [4, 3, 4], event: "trigger:bravo-base" },
  ],
  navGraph: {
    nodes: [
      { id: "n1", position: [-18, 1.2, -18] },
      { id: "n2", position: [18, 1.2, -18] },
      { id: "n3", position: [-18, 1.2, 18] },
      { id: "n4", position: [18, 1.2, 18] },
      { id: "n5", position: [0, 1.2, 0] },
    ],
    links: [
      ["n1", "n5"],
      ["n2", "n5"],
      ["n3", "n5"],
      ["n4", "n5"],
    ],
  },
  markers: [
    { id: "flag-alpha", type: "flag-base", position: [-18, 1.2, -18], team: "alpha" },
    { id: "flag-bravo", type: "flag-base", position: [18, 1.2, 18], team: "bravo" },
  ],
  lighting: {
    backgroundColor: "#090d13",
    fogColor: "#090d13",
    fogNear: 24,
    fogFar: 68,
  },
  missions: [arenaSurvivalMission],
  proceduralFactory: ({ scene, octree }) => {
    const levelRoot = new Group();
    levelRoot.name = "CortexArena01";

    const makeBox = (
      size: [number, number, number],
      position: [number, number, number],
      material: MeshStandardMaterial,
      rotationY = 0
    ) => {
      const mesh = new Mesh(new BoxGeometry(...size), material.clone());
      mesh.position.set(...position);
      mesh.rotation.y = rotationY;
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      levelRoot.add(mesh);
      return mesh;
    };

    const floor = new Mesh(new BoxGeometry(52, 2, 52), floorMaterial.clone());
    floor.position.set(0, -1, 0);
    floor.receiveShadow = true;
    levelRoot.add(floor);

    [
      [0, 6, -25, 52, 12, 2],
      [0, 6, 25, 52, 12, 2],
      [-25, 6, 0, 2, 12, 52],
      [25, 6, 0, 2, 12, 52],
    ].forEach(([x, y, z, w, h, d]) => {
      makeBox([w, h, d], [x, y, z], shadowMaterial);
    });

    makeBox([12, 2, 12], [0, 1, 0], platformMaterial);
    makeBox([10, 1.5, 4], [-16, 2.25, -8], platformMaterial);
    makeBox([10, 1.5, 4], [16, 2.25, 8], platformMaterial);
    makeBox([6, 4, 6], [14, 2, -14], platformMaterial);
    makeBox([6, 4, 6], [-14, 2, 14], platformMaterial);
    makeBox([8, 0.8, 3], [-8, 1.8, 10], platformMaterial, -0.45);
    makeBox([8, 0.8, 3], [8, 1.8, -10], platformMaterial, 0.45);

    for (let i = -18; i <= 18; i += 12) {
      makeBox([2, 6, 2], [i, 3, -6], shadowMaterial);
      makeBox([2, 6, 2], [i, 3, 6], shadowMaterial);
    }

    for (let x = -20; x <= 20; x += 10) {
      const strip = new Mesh(new BoxGeometry(1.6, 0.1, 16), accentMaterial.clone());
      strip.position.set(x, 0.05, 0);
      levelRoot.add(strip);
    }

    for (let i = 0; i < 8; i += 1) {
      const angle = (i / 8) * Math.PI * 2;
      makeBox([1.1, 3.2, 1.1], [Math.cos(angle) * 9.5, 1.6, Math.sin(angle) * 9.5], platformMaterial);
    }

    scene.add(levelRoot);
    octree.fromGraphNode(levelRoot);

    return {
      levelRoot,
      enemySpawns: [
        [-18, 1.2, -18],
        [18, 1.2, -18],
        [-18, 1.2, 18],
        [18, 1.2, 18],
        [0, 2.8, -12],
        [0, 2.8, 12],
        [-14, 4.4, 14],
        [14, 4.4, -14],
      ],
    };
  },
};